import enClients from './locales/en/clients.json';
import enComponents from './locales/en/components.json';
import enDocbuilder from './locales/en/docbuilder.json';
import enSessions from './locales/en/sessions.json';

import type { Locale } from '@askableorg/i18n';
import type { ResourceKey } from 'i18next';

export type AppNamespace = keyof typeof EN_RESOURCES;

type LocaleLoaders = Record<AppNamespace, () => Promise<{ default: ResourceKey }>>;

const EN_RESOURCES = {
  sessions: enSessions,
  clients: enClients,
  components: enComponents,
  docbuilder: enDocbuilder,
};

// One static import() for each file, so that every bundler emits one chunk for each locale and
// namespace. A template-literal import() is not handled the same way by Vite and Next.
export const LOCALE_LOADERS: Record<Exclude<Locale, 'en'>, LocaleLoaders> = {
  ar: {
    clients: () => import('./locales/ar/clients.json'),
    components: () => import('./locales/ar/components.json'),
    docbuilder: () => import('./locales/ar/docbuilder.json'),
    sessions: () => import('./locales/ar/sessions.json'),
  },
  cs: {
    clients: () => import('./locales/cs/clients.json'),
    components: () => import('./locales/cs/components.json'),
    docbuilder: () => import('./locales/cs/docbuilder.json'),
    sessions: () => import('./locales/cs/sessions.json'),
  },
  da: {
    clients: () => import('./locales/da/clients.json'),
    components: () => import('./locales/da/components.json'),
    docbuilder: () => import('./locales/da/docbuilder.json'),
    sessions: () => import('./locales/da/sessions.json'),
  },
  de: {
    clients: () => import('./locales/de/clients.json'),
    components: () => import('./locales/de/components.json'),
    docbuilder: () => import('./locales/de/docbuilder.json'),
    sessions: () => import('./locales/de/sessions.json'),
  },
  es: {
    clients: () => import('./locales/es/clients.json'),
    components: () => import('./locales/es/components.json'),
    docbuilder: () => import('./locales/es/docbuilder.json'),
    sessions: () => import('./locales/es/sessions.json'),
  },
  fr: {
    clients: () => import('./locales/fr/clients.json'),
    components: () => import('./locales/fr/components.json'),
    docbuilder: () => import('./locales/fr/docbuilder.json'),
    sessions: () => import('./locales/fr/sessions.json'),
  },
  hi: {
    clients: () => import('./locales/hi/clients.json'),
    components: () => import('./locales/hi/components.json'),
    docbuilder: () => import('./locales/hi/docbuilder.json'),
    sessions: () => import('./locales/hi/sessions.json'),
  },
  hu: {
    clients: () => import('./locales/hu/clients.json'),
    components: () => import('./locales/hu/components.json'),
    docbuilder: () => import('./locales/hu/docbuilder.json'),
    sessions: () => import('./locales/hu/sessions.json'),
  },
  it: {
    clients: () => import('./locales/it/clients.json'),
    components: () => import('./locales/it/components.json'),
    docbuilder: () => import('./locales/it/docbuilder.json'),
    sessions: () => import('./locales/it/sessions.json'),
  },
  ja: {
    clients: () => import('./locales/ja/clients.json'),
    components: () => import('./locales/ja/components.json'),
    docbuilder: () => import('./locales/ja/docbuilder.json'),
    sessions: () => import('./locales/ja/sessions.json'),
  },
  ko: {
    clients: () => import('./locales/ko/clients.json'),
    components: () => import('./locales/ko/components.json'),
    docbuilder: () => import('./locales/ko/docbuilder.json'),
    sessions: () => import('./locales/ko/sessions.json'),
  },
  nl: {
    clients: () => import('./locales/nl/clients.json'),
    components: () => import('./locales/nl/components.json'),
    docbuilder: () => import('./locales/nl/docbuilder.json'),
    sessions: () => import('./locales/nl/sessions.json'),
  },
  pt: {
    clients: () => import('./locales/pt/clients.json'),
    components: () => import('./locales/pt/components.json'),
    docbuilder: () => import('./locales/pt/docbuilder.json'),
    sessions: () => import('./locales/pt/sessions.json'),
  },
  sk: {
    clients: () => import('./locales/sk/clients.json'),
    components: () => import('./locales/sk/components.json'),
    docbuilder: () => import('./locales/sk/docbuilder.json'),
    sessions: () => import('./locales/sk/sessions.json'),
  },
  tr: {
    clients: () => import('./locales/tr/clients.json'),
    components: () => import('./locales/tr/components.json'),
    docbuilder: () => import('./locales/tr/docbuilder.json'),
    sessions: () => import('./locales/tr/sessions.json'),
  },
  'zh-CN': {
    clients: () => import('./locales/zh-CN/clients.json'),
    components: () => import('./locales/zh-CN/components.json'),
    docbuilder: () => import('./locales/zh-CN/docbuilder.json'),
    sessions: () => import('./locales/zh-CN/sessions.json'),
  },
  'zh-HK': {
    clients: () => import('./locales/zh-HK/clients.json'),
    components: () => import('./locales/zh-HK/components.json'),
    docbuilder: () => import('./locales/zh-HK/docbuilder.json'),
    sessions: () => import('./locales/zh-HK/sessions.json'),
  },
};

export const isAppNamespace = (ns: string): ns is AppNamespace => ns in EN_RESOURCES;

/** English is the fallback language, thus it stays in the bundle. */
export const bundledResources = (namespaces: readonly AppNamespace[]) => ({
  en: Object.fromEntries(namespaces.map(ns => [ns, EN_RESOURCES[ns]])),
});
